import { Controller } from "@hotwired/stimulus"

// Controller for the repository page that handles SSE streaming
export default class extends Controller {
  static targets = ["tableBody", "rebuildButton", "cardView"]

  connect() {
    this.eventSource = null
    this.fileRows = new Map()
    this.folderRows = new Map()  // Track folder rows
    this.folderState = new Map() // Track expanded/collapsed state
    this.timers = new Map() // Track elapsed time timers
    this.isRebuilding = false

    // Listen for upload:ready event from the popup form
    this.handleUploadReady = this.handleUploadReady.bind(this)
    window.addEventListener('upload:ready', this.handleUploadReady)

    // Build map of existing rows by document name
    this.indexExistingRows()
  }

  disconnect() {
    window.removeEventListener('upload:ready', this.handleUploadReady)
    this.stopAllTimers()
    if (this.eventSource) {
      this.eventSource.close()
    }
  }

  // Index existing table rows for RAG rebuild updates and upload merging
  indexExistingRows() {
    // Index file rows
    const fileRows = this.tableBodyTarget.querySelectorAll('.file-row')
    fileRows.forEach(row => {
      // Extract document name from the first cell (could be link or span)
      const nameCell = row.querySelector('.text-gray-800.font-medium, .text-blue-600.font-medium, a.font-medium')
      if (nameCell) {
        const name = nameCell.textContent.trim()
        this.fileRows.set(name, row)
      }
    })

    // Index folder rows
    const folderRows = this.tableBodyTarget.querySelectorAll('.folder-row')
    folderRows.forEach(row => {
      const folderPath = row.dataset.folder
      if (folderPath) {
        this.folderRows.set(folderPath, row)
        this.folderState.set(folderPath, row.dataset.expanded !== 'false')
      }
    })
  }

  // Start RAG rebuild with SSE streaming
  startRebuild() {
    if (this.isRebuilding) return

    if (!confirm('Rebuild the RAG knowledge base? This will re-embed all documents.')) {
      return
    }

    this.isRebuilding = true

    // Disable button
    if (this.hasRebuildButtonTarget) {
      this.rebuildButtonTarget.disabled = true
      this.rebuildButtonTarget.innerHTML = `
        <svg class="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Rebuilding...
      `
    }

    // Re-index existing rows in case the page was just loaded
    this.indexExistingRows()

    // Start SSE connection for RAG rebuild
    const url = '/repositories/stream_rebuild_rag'
    this.eventSource = new EventSource(url)

    this.eventSource.addEventListener('start', (e) => {
      const data = JSON.parse(e.data)
      console.log('RAG rebuild started:', data.message)
    })

    this.eventSource.addEventListener('phase', (e) => {
      const data = JSON.parse(e.data)
      console.log('Phase:', data.phase, '-', data.message)
    })

    this.eventSource.addEventListener('info', (e) => {
      const data = JSON.parse(e.data)
      console.log('Info:', data.message)
    })

    this.eventSource.addEventListener('doc_indexing', (e) => {
      const data = JSON.parse(e.data)
      this.handleDocIndexing(data)
    })

    this.eventSource.addEventListener('doc_indexed', (e) => {
      const data = JSON.parse(e.data)
      this.handleDocIndexed(data)
    })

    this.eventSource.addEventListener('complete', (e) => {
      const data = JSON.parse(e.data)
      this.handleRebuildComplete(data)
    })

    this.eventSource.addEventListener('error', (e) => {
      if (e.data) {
        const data = JSON.parse(e.data)
        this.showFlash(data.message, "error")
      }
      this.cleanupRebuild()
    })

    this.eventSource.onerror = (e) => {
      console.error('SSE connection error:', e)
      this.cleanupRebuild()
    }
  }

  handleDocIndexing(data) {
    // Find row by document name and update status to "Indexing..."
    const row = this.findRowByName(data.name)
    if (row) {
      this.updateRowStatus(row, 'Indexing...', 'bg-blue-100 text-blue-800')
      this.startTimer(data.name, row)
    }
  }

  handleDocIndexed(data) {
    // Find row by document name and update status to "Indexed"
    const row = this.findRowByName(data.name)
    if (row) {
      const elapsed = this.stopTimer(data.name)
      const elapsedStr = elapsed ? ` (${this.formatTime(elapsed)})` : ''

      this.updateRowStatus(row, 'Indexed', 'bg-green-100 text-green-800')
      const descCell = row.querySelector('.desc-cell')
      if (descCell) {
        // Preserve existing content but add indexing note
        const existingText = descCell.textContent.trim()
        if (existingText && existingText !== '-' && !existingText.startsWith('✓')) {
          descCell.innerHTML = `<span class="text-sm text-green-600">✓ Indexed${elapsedStr}</span> | ${existingText}`
        } else {
          descCell.innerHTML = `<span class="text-sm text-green-600">✓ Indexed${elapsedStr}</span>`
        }
      }
    }
  }

  handleRebuildComplete(data) {
    this.stopAllTimers()
    this.showFlash(data.message || 'RAG rebuild complete!', "success")

    // Surface documents that have no processed output (never indexed). Keep the
    // page so the warning stays visible instead of reloading it away.
    if (data.warnings && data.warnings.length > 0) {
      setTimeout(() => {
        this.showFlash(`${data.warnings.length} document(s) not indexed: ${data.warnings.join('; ')}`, "warning")
      }, 700)
      this.cleanupRebuild()
      return
    }

    this.cleanupRebuild()

    // Reload page after a short delay to refresh chunk counts
    setTimeout(() => {
      window.location.reload()
    }, 2000)
  }

  cleanupRebuild() {
    this.isRebuilding = false

    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }

    // Re-enable button
    if (this.hasRebuildButtonTarget) {
      this.rebuildButtonTarget.disabled = false
      this.rebuildButtonTarget.innerHTML = `
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        Rebuild RAG
      `
    }
  }

  // Find a row by document name (handles various name formats)
  findRowByName(name) {
    // First try exact match from our map
    if (this.fileRows.has(name)) {
      return this.fileRows.get(name)
    }

    // Try to find by searching the table
    const rows = this.tableBodyTarget.querySelectorAll('[class*="flex items-center border-b"]')
    for (const row of rows) {
      const nameCell = row.querySelector('.text-gray-800.font-medium, .text-blue-600.font-medium, a.font-medium')
      if (nameCell && nameCell.textContent.trim() === name) {
        this.fileRows.set(name, row)
        return row
      }
    }

    return null
  }

  handleUploadReady(event) {
    const { sessionId, streamUrl, importMode, warnings } = event.detail

    if (warnings && warnings.length > 0) {
      this.showFlash(`Warning: ${warnings.join('\n')}`, "warning")
    }

    // Only clear table for restart mode (remove all and reimport)
    if (importMode === 'restart') {
      this.clearTable()
    } else {
      // Re-index existing rows for merge/replace mode
      this.indexExistingRows()
    }

    this.startStream(streamUrl, sessionId)
  }

  startStream(streamUrl, sessionId) {
    const url = `${streamUrl}?session_id=${sessionId}`
    this.eventSource = new EventSource(url)

    this.eventSource.addEventListener('info', (e) => {
      const data = JSON.parse(e.data)
      console.log('Info:', data.message)
    })

    this.eventSource.addEventListener('folders', (e) => {
      const data = JSON.parse(e.data)
      this.handleFolders(data)
    })

    this.eventSource.addEventListener('extracted', (e) => {
      const data = JSON.parse(e.data)
      this.handleExtracted(data)
    })

    this.eventSource.addEventListener('processing_start', (e) => {
      const data = JSON.parse(e.data)
      this.handleProcessingStart(data)
    })

    this.eventSource.addEventListener('processing_done', (e) => {
      const data = JSON.parse(e.data)
      this.handleProcessingDone(data)
    })

    this.eventSource.addEventListener('indexing_start', (e) => {
      const data = JSON.parse(e.data)
      this.handleIndexingStart(data)
    })

    this.eventSource.addEventListener('phase', (e) => {
      const data = JSON.parse(e.data)
      console.log('Phase:', data.phase, '-', data.message)
    })

    this.eventSource.addEventListener('processing_error', (e) => {
      const data = JSON.parse(e.data)
      this.handleProcessingError(data)
    })

    this.eventSource.addEventListener('complete', (e) => {
      const data = JSON.parse(e.data)
      this.handleComplete(data)
    })

    this.eventSource.addEventListener('error', (e) => {
      if (e.data) {
        const data = JSON.parse(e.data)
        this.showFlash(data.message, "error")
      }
      this.cleanup()
    })

    this.eventSource.onerror = (e) => {
      console.error('SSE connection error:', e)
    }
  }

  handleFolders(data) {
    // Remove empty message if present
    const emptyMsg = document.getElementById('empty-message')
    if (emptyMsg) emptyMsg.remove()

    // Check if TOP folder row already exists (from server-rendered page)
    let existingTopRow = this.tableBodyTarget.querySelector('[data-folder="TOP"]')
    if (!existingTopRow) {
      // Create TOP folder row if there are any files (root or in folders)
      const topRow = this.createFolderRow('TOP', 0)
      this.tableBodyTarget.appendChild(topRow)
      this.folderRows.set('TOP', topRow)
      this.folderState.set('TOP', true) // expanded by default
    } else {
      // Use existing TOP folder row
      this.folderRows.set('TOP', existingTopRow)
      this.folderState.set('TOP', existingTopRow.dataset.expanded !== 'false')
    }

    // Create folder rows for each subfolder (only if they don't already exist)
    data.folders.forEach(folderPath => {
      const existingRow = this.tableBodyTarget.querySelector(`[data-folder="${CSS.escape(folderPath)}"]`)
      if (!existingRow) {
        const depth = folderPath.split('/').length
        const row = this.createFolderRow(folderPath, depth)
        this.tableBodyTarget.appendChild(row)
        this.folderRows.set(folderPath, row)
        this.folderState.set(folderPath, true) // expanded by default
      } else {
        // Use existing folder row
        this.folderRows.set(folderPath, existingRow)
        this.folderState.set(folderPath, existingRow.dataset.expanded !== 'false')
      }
    })
  }

  handleExtracted(data) {
    // Determine which folder this file belongs to
    const folderPath = data.folder_path || null
    const depth = folderPath ? folderPath.split('/').length + 1 : 1

    // Check if a row for this file already exists (update mode)
    let existingRow = this.fileRows.get(data.name)
    if (!existingRow) {
      // Also try to find by ID pattern
      const rowId = `doc-row-${data.name.replace(/[^a-zA-Z0-9]/g, '-')}`
      existingRow = document.getElementById(rowId)
    }

    if (existingRow) {
      // Update existing row status instead of creating a new one
      this.updateRowStatus(existingRow, 'Extracted', 'bg-yellow-100 text-yellow-800')
      this.fileRows.set(data.name, existingRow)
      return
    }

    const row = this.createRow(data.name, data.source_type, 'Extracted', depth, folderPath)

    if (folderPath === null) {
      // ROOT file: insert after TOP and other root files, but BEFORE any subfolder rows
      const topRow = this.folderRows.get('TOP')
      if (topRow) {
        // Find the first subfolder row (to insert before it) or the last root file (to insert after it)
        let insertAfter = topRow
        let sibling = topRow.nextElementSibling

        while (sibling) {
          const isFolder = sibling.classList.contains('folder-row')
          const siblingFolder = sibling.dataset.folderPath

          // Stop BEFORE any subfolder row
          if (isFolder && sibling.dataset.folderPath !== 'TOP') {
            break
          }
          // Stop if we hit a file that belongs to a subfolder
          if (!isFolder && siblingFolder) {
            break
          }
          // Continue past other root files
          insertAfter = sibling
          sibling = sibling.nextElementSibling
        }
        insertAfter.after(row)
      } else {
        this.tableBodyTarget.appendChild(row)
      }
    } else {
      // SUBFOLDER file: insert after the folder row and any existing files in that folder
      const folderRow = this.folderRows.get(folderPath)
      if (folderRow) {
        let insertAfter = folderRow
        let sibling = folderRow.nextElementSibling

        while (sibling) {
          const isFolder = sibling.classList.contains('folder-row')
          const siblingFolder = sibling.dataset.folderPath

          // Stop at any folder row (next folder starts)
          if (isFolder) {
            break
          }
          // Stop if sibling belongs to a different folder
          if (siblingFolder !== folderPath) {
            break
          }
          // Continue past files in the same folder
          insertAfter = sibling
          sibling = sibling.nextElementSibling
        }
        insertAfter.after(row)
      } else {
        this.tableBodyTarget.appendChild(row)
      }
    }

    this.fileRows.set(data.name, row)
  }

  handleProcessingStart(data) {
    const row = this.fileRows.get(data.name)
    if (row) {
      this.updateRowStatus(row, 'Processing...', 'bg-blue-100 text-blue-800')
      // Start elapsed timer
      this.startTimer(data.name, row)
    }
  }

  handleIndexingStart(data) {
    const row = this.fileRows.get(data.name)
    if (row) {
      this.updateRowStatus(row, 'Indexing...', 'bg-indigo-100 text-indigo-800')
      const descCell = row.querySelector('.desc-cell')
      if (descCell) {
        descCell.innerHTML = '<span class="text-sm text-indigo-600">Adding to knowledge base...</span>'
      }
    }
  }

  handleProcessingDone(data) {
    const row = this.fileRows.get(data.name)
    if (row) {
      // Stop timer and get final elapsed time
      const elapsed = this.stopTimer(data.name)
      const elapsedStr = elapsed ? ` (${this.formatTime(elapsed)})` : ''
      const chunksStr = data.chunks ? `, ${data.chunks} chunks` : ''

      this.updateRowStatus(row, 'Indexed', 'bg-green-100 text-green-800')
      const descCell = row.querySelector('.desc-cell')
      if (descCell) {
        descCell.innerHTML = `<span class="text-sm text-green-600">✓ Done${elapsedStr}${chunksStr}</span>`
      }
    }
  }

  handleProcessingError(data) {
    const row = this.fileRows.get(data.name)
    if (row) {
      // Stop timer
      this.stopTimer(data.name)

      this.updateRowStatus(row, 'Error', 'bg-red-100 text-red-800')
      const descCell = row.querySelector('.desc-cell')
      if (descCell) {
        descCell.innerHTML = `<span class="text-sm text-red-600">✗ ${this.escapeHtml(data.error)}</span>`
      }
    }
  }

  handleComplete(data) {
    this.stopAllTimers()

    // Build detailed completion message
    let message = `Completed! ${data.total} document(s) loaded`

    // Add chunks indexed count
    if (data.chunks_indexed > 0) {
      message += `, ${data.chunks_indexed} chunks indexed`
    }

    // Add wrapper stats if available (indicates backup restore)
    if (data.with_existing_wrapper > 0 || data.with_new_wrapper > 0) {
      const parts = []
      if (data.with_existing_wrapper > 0) {
        parts.push(`${data.with_existing_wrapper} with existing metadata`)
      }
      if (data.with_new_wrapper > 0) {
        parts.push(`${data.with_new_wrapper} with new wrappers`)
      }
      message += ` (${parts.join(', ')})`
    }

    this.showFlash(message, "success")

    // Show warnings if any
    if (data.warnings && data.warnings.length > 0) {
      // Show each warning after a small delay
      setTimeout(() => {
        data.warnings.forEach(warning => {
          this.showFlash(warning, "warning")
        })
      }, 500)
    }

    // Documents that failed processing are never silently dropped: report them
    // and keep the page (with its red per-row reasons) so the user can act.
    if (data.failures && data.failures.length > 0) {
      const list = data.failures.map(f => `${f.name} — ${f.error}`).join('; ')
      setTimeout(() => {
        this.showFlash(`${data.failures.length} document(s) NOT indexed: ${list}`, "error")
      }, 800)
      this.cleanup()
      return // skip auto-reload so the failures stay visible
    }

    this.cleanup()

    // Reload page after a short delay to refresh status and show Rebuild RAG button
    setTimeout(() => {
      window.location.reload()
    }, 2500)
  }

  // Timer methods for elapsed time display
  startTimer(name, row) {
    const startTime = Date.now()
    const descCell = row.querySelector('.desc-cell')

    const intervalId = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000)
      if (descCell) {
        descCell.innerHTML = `<span class="text-sm text-blue-600">Processing... ${this.formatTime(elapsed)}</span>`
      }
    }, 1000)

    this.timers.set(name, { intervalId, startTime })

    // Show initial state immediately
    if (descCell) {
      descCell.innerHTML = '<span class="text-sm text-blue-600">Processing... 0:00</span>'
    }
  }

  stopTimer(name) {
    const timer = this.timers.get(name)
    if (timer) {
      clearInterval(timer.intervalId)
      const elapsed = Math.floor((Date.now() - timer.startTime) / 1000)
      this.timers.delete(name)
      return elapsed
    }
    return null
  }

  stopAllTimers() {
    this.timers.forEach((timer) => {
      clearInterval(timer.intervalId)
    })
    this.timers.clear()
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  createFolderRow(folderPath, depth) {
    const row = document.createElement('div')
    row.className = 'flex items-center border-b border-gray-200 bg-gray-100 folder-row'
    row.id = `folder-row-${folderPath.replace(/[^a-zA-Z0-9]/g, '-')}`
    row.dataset.folderPath = folderPath
    row.dataset.depth = depth

    const indent = depth * 20  // 20px per level
    const displayName = folderPath === 'TOP' ? 'TOP' : folderPath.split('/').pop()

    row.innerHTML = `
      <div class="w-5/12 px-4 py-2 flex items-center" style="padding-left: ${16 + indent}px;">
        <button type="button" class="folder-toggle inline-flex items-center text-gray-700 hover:text-gray-900"
                data-action="click->repository-stream#toggleFolder"
                data-folder-path="${this.escapeHtml(folderPath)}">
          <svg class="folder-icon-expanded w-4 h-4 mr-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
          <svg class="folder-icon-collapsed w-4 h-4 mr-1 transition-transform hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
          <span class="font-semibold text-sm uppercase tracking-wide">${this.escapeHtml(displayName)}</span>
        </button>
      </div>
      <div class="w-1/12 px-4 py-2 text-center">
        <span class="text-gray-400">-</span>
      </div>
      <div class="w-1/6 px-4 py-2">
        <span class="text-gray-400">-</span>
      </div>
      <div class="w-1/3 px-4 py-2">
        <span class="text-gray-400">-</span>
      </div>
    `
    return row
  }

  toggleFolder(event) {
    const button = event.currentTarget
    const folderPath = button.dataset.folderPath
    const isExpanded = this.folderState.get(folderPath)

    // Toggle state
    this.folderState.set(folderPath, !isExpanded)

    // Update icon
    const expandedIcon = button.querySelector('.folder-icon-expanded')
    const collapsedIcon = button.querySelector('.folder-icon-collapsed')
    if (isExpanded) {
      expandedIcon.classList.add('hidden')
      collapsedIcon.classList.remove('hidden')
    } else {
      expandedIcon.classList.remove('hidden')
      collapsedIcon.classList.add('hidden')
    }

    // Show/hide children
    this.updateChildVisibility(folderPath, !isExpanded)
  }

  updateChildVisibility(folderPath, visible) {
    const folderRow = this.folderRows.get(folderPath)
    if (!folderRow) return

    const folderDepth = parseInt(folderRow.dataset.depth)
    let sibling = folderRow.nextElementSibling

    while (sibling) {
      const siblingDepth = parseInt(sibling.dataset.depth || '999')
      const siblingFolder = sibling.dataset.folderPath

      // Stop if we hit a folder at same or lower depth
      if (sibling.classList.contains('folder-row') && siblingDepth <= folderDepth) {
        break
      }

      // Check if this is a direct child or nested child
      const isChild = sibling.classList.contains('folder-row')
        ? siblingFolder?.startsWith(folderPath + '/') || (folderPath === 'TOP' && siblingDepth === 1)
        : (siblingFolder === folderPath || siblingFolder === null && folderPath === 'TOP')

      const isNestedChild = !isChild && (
        siblingFolder?.startsWith(folderPath + '/') ||
        (folderPath === 'TOP' && siblingDepth > 0)
      )

      if (isChild || isNestedChild) {
        sibling.style.display = visible ? '' : 'none'

        // If hiding, also collapse any nested folders
        if (!visible && sibling.classList.contains('folder-row')) {
          this.folderState.set(siblingFolder, false)
          const expandedIcon = sibling.querySelector('.folder-icon-expanded')
          const collapsedIcon = sibling.querySelector('.folder-icon-collapsed')
          if (expandedIcon) expandedIcon.classList.add('hidden')
          if (collapsedIcon) collapsedIcon.classList.remove('hidden')
        }
      }

      sibling = sibling.nextElementSibling
    }
  }

  createRow(name, sourceType, status, depth = 0, folderPath = null) {
    const row = document.createElement('div')
    row.className = 'flex items-center border-b border-gray-200 hover:bg-gray-50 file-row'
    row.id = `doc-row-${name.replace(/[^a-zA-Z0-9]/g, '-')}`
    row.dataset.depth = depth
    if (folderPath) {
      row.dataset.folderPath = folderPath
    }

    const indent = depth * 20  // 20px per level

    row.innerHTML = `
      <div class="w-1/4 px-4 py-3" style="padding-left: ${16 + indent}px;">
        <span class="text-gray-800 font-medium">${this.escapeHtml(name)}</span>
      </div>
      <div class="w-1/6 px-4 py-3">
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${this.getTypeColor(sourceType)}">
          ${sourceType.toUpperCase()}
        </span>
      </div>
      <div class="w-1/12 px-4 py-3 text-center">
        <span class="text-gray-600">-</span>
      </div>
      <div class="w-1/6 px-4 py-3">
        <span class="status-badge inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          ${status}
        </span>
      </div>
      <div class="w-1/3 px-4 py-3 desc-cell">
        <span class="text-sm text-gray-400 italic">-</span>
      </div>
    `
    return row
  }

  updateRowStatus(row, status, colorClass) {
    const badge = row.querySelector('.status-badge')
    if (badge) {
      badge.textContent = status
      badge.className = `status-badge inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`
    }
  }

  getTypeColor(type) {
    const colors = {
      'pdf': 'bg-red-100 text-red-800',
      'docx': 'bg-blue-100 text-blue-800',
      'pptx': 'bg-orange-100 text-orange-800',
      'mp3': 'bg-purple-100 text-purple-800',
      'mp4': 'bg-purple-100 text-purple-800',
      'm4v': 'bg-purple-100 text-purple-800',
      'url': 'bg-green-100 text-green-800',
      'youtube': 'bg-red-100 text-red-800',
      'txt': 'bg-yellow-100 text-yellow-800',
      'markdown': 'bg-green-100 text-green-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  showFlash(message, type) {
    const flashContainer = document.getElementById('flash_messages')
    if (!flashContainer) return

    // Clear any existing flash messages first
    flashContainer.innerHTML = ''

    const bgColor = type === 'error' ? 'bg-red-100 border-red-400 text-red-700'
                  : type === 'warning' ? 'bg-yellow-100 border-yellow-400 text-yellow-700'
                  : 'bg-green-100 border-green-400 text-green-700'

    const icon = type === 'error' ? '✗'
               : type === 'warning' ? '⚠'
               : '✓'

    const flash = document.createElement('div')
    flash.className = `${bgColor} px-4 py-3 rounded relative mb-4 border shadow-md`
    flash.innerHTML = `
      <span class="font-medium">${icon}</span>
      <span class="ml-2 inline-block align-top">${this.escapeHtml(message).replace(/\n/g, '<br>')}</span>
      <button type="button" class="absolute top-0 bottom-0 right-0 px-4 py-3" onclick="this.parentElement.remove()">
        <span class="text-xl">&times;</span>
      </button>
    `
    flashContainer.appendChild(flash)

    // Scroll to top to show the message
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Toggle folder visibility for the permanent repository view (server-rendered)
  // Works with table view (.folder-row) and card view (.folder-container)
  toggleRepoFolder(event) {
    const button = event.currentTarget
    const folderName = button.dataset.folder

    // Support table view (.folder-row) and card view (.folder-container)
    const folderRow = button.closest('.folder-row')
    const folderContainer = button.closest('.folder-container')

    // Determine the element that tracks expanded state
    const folderElement = folderRow || folderContainer
    if (!folderElement) return

    const isExpanded = folderElement.dataset.expanded === 'true'
    folderElement.dataset.expanded = (!isExpanded).toString()

    // Update icon
    const expandedIcon = button.querySelector('.folder-icon-expanded')
    const collapsedIcon = button.querySelector('.folder-icon-collapsed')
    if (isExpanded) {
      expandedIcon.classList.add('hidden')
      collapsedIcon.classList.remove('hidden')
    } else {
      expandedIcon.classList.remove('hidden')
      collapsedIcon.classList.add('hidden')
    }

    // For card view: toggle the folder-contents inside the same container
    if (folderContainer) {
      const folderContents = folderContainer.querySelector(':scope > .folder-contents')
      if (folderContents) {
        folderContents.style.display = isExpanded ? 'none' : ''
      }
      return // Card view handles its own children, no need for table logic
    }

    // For table view: show/hide children rows
    this.toggleRepoFolderChildren(folderName, !isExpanded)
  }

  toggleRepoFolderChildren(folderName, visible) {
    // Handle table view only (file-row, folder-row)
    // Card view is self-contained and handles its own children in toggleRepoFolder
    if (this.hasTableBodyTarget) {
      const rows = this.tableBodyTarget.querySelectorAll('.file-row, .folder-row')
      this.toggleChildElements(rows, folderName, visible, 'file-row', 'folder-row')
    }
  }

  toggleChildElements(elements, folderName, visible, fileClass, folderClass) {
    elements.forEach(el => {
      const elFolder = el.dataset.folder

      // Check if this element is a child of the folder being toggled
      let isChild = false

      if (folderName === 'TOP') {
        // TOP folder: show/hide everything (all files and all subfolders)
        if (el.classList.contains(fileClass)) {
          isChild = true
        }
        if (el.classList.contains(folderClass) && elFolder !== 'TOP') {
          isChild = true
        }
      } else {
        // Subfolder: show/hide files in this folder AND nested subfolders
        if (el.classList.contains(fileClass)) {
          // File is child if its folder matches or starts with this folder
          if (elFolder === folderName || (elFolder && elFolder.startsWith(folderName + '/'))) {
            isChild = true
          }
        }
        if (el.classList.contains(folderClass)) {
          // Folder is child if it starts with this folder path
          if (elFolder && elFolder.startsWith(folderName + '/')) {
            isChild = true
          }
        }
      }

      if (isChild) {
        el.style.display = visible ? '' : 'none'

        // If hiding, also collapse any nested folders (update their visual state)
        if (!visible && el.classList.contains(folderClass)) {
          el.dataset.expanded = 'false'
          const expandedIcon = el.querySelector('.folder-icon-expanded')
          const collapsedIcon = el.querySelector('.folder-icon-collapsed')
          if (expandedIcon) expandedIcon.classList.add('hidden')
          if (collapsedIcon) collapsedIcon.classList.remove('hidden')
        }
      }
    })
  }

  clearTable() {
    this.tableBodyTarget.innerHTML = ''
    this.fileRows.clear()
    this.folderRows.clear()
    this.folderState.clear()
  }

  cleanup() {
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }
  }
}
